import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthService } from '@/services/auth.service';

export interface User {
  email: string;
  name?: string;
  sub: string;
  groups?: string[];
  // Add other Cognito user attributes as needed
}

interface UserContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  signOut: () => Promise<void>;
}

export const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authenticated = await AuthService.isAuthenticated();
        if (authenticated) {
          const userInfo = await AuthService.getUserInfo();
          setUser({
            email: userInfo.email,
            name: userInfo.username,
            sub: userInfo.userId,
            groups: userInfo.groups,
          });
        }
      } catch (error) {
        console.error('Error checking auth:', error);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  const signOut = async () => {
    await AuthService.signOut();
    setUser(null);
    globalThis.location.href = '/';
  };

  const isAuthenticated = !!user;

  const contextValue = React.useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    setUser,
    signOut
  }), [user, isAuthenticated, isLoading]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within UserProvider');
  return context;
};
